import React from "react";
import { Typography } from "@mui/material";

const ChartBasicsGuide = () => {
  return (
    <>
      <Typography variant="h6" className="section-heading" color="secondary">
        What Are Knitting Charts?
      </Typography>
      <Typography variant="body1">
        Knitting charts are visual representations of knitting patterns. Instead of written
        instructions like "K2, P2, K2, P2," a chart shows you exactly where each stitch goes using
        symbols in a grid.
      </Typography>

      <Typography variant="h6" className="section-heading" color="secondary">
        When to Use Charts
      </Typography>
      <Typography variant="body1">
        Charts are especially useful for:
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Lace Patterns</span>
          <span className="feature-item-description">
            Lace charts show exactly where yarn overs and decreases go, making it easier to see the
            pattern's structure and catch mistakes.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Cable Patterns</span>
          <span className="feature-item-description">
            Cable charts show which stitches cross over others, making complex cable patterns much
            clearer than written instructions.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Colorwork</span>
          <span className="feature-item-description">
            Color charts (like Fair Isle or intarsia) show exactly which color goes in each stitch,
            making it easy to follow complex designs.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Texture Patterns</span>
          <span className="feature-item-description">
            Charts help visualize how texture patterns like bobbles, nupps, or slip stitches create
            the final fabric appearance.
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Advantages of Charts
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Visual Clarity</span>
          <span className="feature-item-description">
            You can see the pattern's structure at a glance, making it easier to understand how the
            design works.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Easier Error Detection</span>
          <span className="feature-item-description">
            Mistakes are often more obvious in a chart—you can see if a stitch is in the wrong place
            visually.
          </span>
        </li>
        <li>
          <span className="feature-item-title">International</span>
          <span className="feature-item-description">
            Charts use symbols, not words, so they work across languages. A chart from a Japanese
            pattern is just as readable as one from an English pattern.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Compact</span>
          <span className="feature-item-description">
            Complex patterns that would take pages of written instructions can fit on a single
            chart.
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Disadvantages of Charts
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Learning Curve</span>
          <span className="feature-item-description">
            If you're used to written patterns, charts can feel unfamiliar at first. It takes
            practice to read them fluently.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Symbol Variations</span>
          <span className="feature-item-description">
            Different designers or publications may use slightly different symbols, so you need to
            check each pattern's key.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Not Ideal for All Patterns</span>
          <span className="feature-item-description">
            Simple patterns (like stockinette or basic ribbing) are often easier to follow in
            written form.
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Chart Symbols Standards
      </Typography>
      <Typography variant="body1">
        Most modern patterns follow Craft Yarn Council (CYC) standards for chart symbols. These
        standards ensure consistency across patterns and make charts more accessible. However, some
        designers use custom symbols, so always check the pattern's stitch key.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Loops & Purls uses CYC standard symbols, making charts created here compatible with
        international knitting standards.
      </Typography>
    </>
  );
};

export default ChartBasicsGuide;

